import { sha256 } from "@noble/hashes/sha2.js";
import { isRemoteFirmwareFile, isRemoteFirmwareRange } from "./RemoteFirmwareFile";

export const DEFAULT_CHUNK_SIZE = 1024 * 1024;

export async function readBlobArrayBuffer(blob: Blob, signal?: AbortSignal): Promise<ArrayBuffer> {
  if (signal?.aborted) throw new DOMException("Operation cancelled", "AbortError");
  if (isRemoteFirmwareRange(blob)) return blob.readArrayBuffer(signal);
  if (typeof blob.arrayBuffer === "function") return blob.arrayBuffer();
  return new Promise<ArrayBuffer>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as ArrayBuffer);
    reader.onerror = () => reject(reader.error ?? new Error("Unable to read Blob"));
    reader.readAsArrayBuffer(blob);
  });
}

export async function* readFileChunks(file: Blob, chunkSize = DEFAULT_CHUNK_SIZE, signal?: AbortSignal): AsyncGenerator<Uint8Array> {
  for (let offset = 0; offset < file.size; offset += chunkSize) {
    if (signal?.aborted) throw new DOMException("Operation cancelled", "AbortError");
    const chunk = await readBlobArrayBuffer(file.slice(offset, Math.min(offset + chunkSize, file.size)));
    yield new Uint8Array(chunk);
  }
}

export function splitBuffer(buffer: Uint8Array, chunkSize: number): Uint8Array[] {
  if (chunkSize <= 0) throw new RangeError("chunkSize must be positive");
  const chunks: Uint8Array[] = [];
  for (let offset = 0; offset < buffer.byteLength; offset += chunkSize) chunks.push(buffer.slice(offset, offset + chunkSize));
  return chunks;
}

export function calculateProgress(completedBytes: number, totalBytes: number): number {
  if (totalBytes <= 0) return 0;
  return Math.min(100, Math.max(0, (completedBytes / totalBytes) * 100));
}

export async function hashFile(file: Blob, onProgress?: (completedBytes: number) => void, signal?: AbortSignal): Promise<string> {
  if (isRemoteFirmwareFile(file)) {
    const hash = sha256.create();
    let completedBytes = 0;
    for (let offset = 0; offset < file.size; offset += DEFAULT_CHUNK_SIZE) {
      if (signal?.aborted) throw new DOMException("Operation cancelled", "AbortError");
      const end = Math.min(offset + DEFAULT_CHUNK_SIZE, file.size);
      hash.update(new Uint8Array(await file.readRange(offset, end, signal)));
      completedBytes = end;
      onProgress?.(completedBytes);
    }
    return Array.from(hash.digest(), (byte) => byte.toString(16).padStart(2, "0")).join("");
  }
  const worker = new Worker(new URL("../../workers/hash.worker.ts", import.meta.url), { type: "module" });
  return new Promise<string>((resolve, reject) => {
    const abort = () => {
      worker.terminate();
      reject(new DOMException("Operation cancelled", "AbortError"));
    };
    if (signal?.aborted) return abort();
    signal?.addEventListener("abort", abort, { once: true });
    worker.onmessage = (event: MessageEvent<{ type: string; hash?: string; completedBytes?: number; error?: string }>) => {
      if (event.data.type === "progress") onProgress?.(event.data.completedBytes ?? 0);
      if (event.data.type === "done") {
        signal?.removeEventListener("abort", abort);
        worker.terminate();
        resolve(event.data.hash ?? "");
      }
      if (event.data.type === "error") {
        signal?.removeEventListener("abort", abort);
        worker.terminate();
        reject(new Error(event.data.error ?? "Hash worker failed"));
      }
    };
    worker.onerror = (event) => {
      signal?.removeEventListener("abort", abort);
      worker.terminate();
      reject(event.error ?? new Error(event.message));
    };
    worker.postMessage({ file });
  });
}
